import { useState, useEffect } from "react";
import {
  Space,
  Spin,
  message,
  Menu,
  Input,
  List,
  Card,
  Button,
  Flex,
  Image,
  Pagination,
  Badge,
  Select,
  Grid,
  Drawer,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  LoadingOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";

import { Link, useNavigate, useLocation } from "react-router-dom";

import PageTitle from "../../../Components/PageTitle/pageTitle";
import publicDataServices from "../../../APIs/publicDataServices";

import { settings } from "../../../Services/apiSetting";
import {
  getProductUrl,
  getProductFilters,
  getProductsUrl,
} from "../../../Components/Common/helpers";
import StaticSeoManager from "../../../Components/Common/staticSEOManager";
import {
  addToCart,
  getDefaultSelectedOptions,
} from "../../../Components/Common/addToCart";

const { Search } = Input;

const { useBreakpoint } = Grid;

const PAGE_SIZE = 20;

const findByKey = (categories, key) => {
  for (const category of categories) {
    if (`cat-${category.Id}` === key) return { category, subCategory: null };
    const subCategory = (category.SubCategories || []).find(
      (sub) => `sub-${sub.Id}` === key,
    );
    if (subCategory) return { category, subCategory };
  }
  return null;
};

const Products = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const navigate = useNavigate();
  const location = useLocation();
  const filters = getProductFilters(location.search);
  const { selectedKey, page, search, sort } = filters;

  const [messageApi, contextHolder] = message.useMessage();

  const [open, setOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [isPaginationLoading, setIsPaginationLoading] = useState(true);
  const [products, setProducts] = useState([]);

  const [openKeys, setOpenKeys] = useState([]);

  const [combinedCategories, setCombinedCategories] = useState([]);

  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState(search);
  const selectedCategory = findByKey(combinedCategories, selectedKey);
  const categoryName =
    selectedCategory?.subCategory?.SubCategoryName ||
    (selectedKey !== "cat-0" ? selectedCategory?.category?.CategoryName : null);
  const legacyCategory = !location.search && location.state?.category;
  const legacyKey =
    legacyCategory &&
    (legacyCategory.SelectedKey || `cat-${legacyCategory.Id}`);
  const validKey =
    legacyKey && findByKey(combinedCategories, legacyKey)
      ? legacyKey
      : selectedCategory
        ? selectedKey
        : "cat-0";
  const validCategory = findByKey(combinedCategories, validKey);
  const filtersUrl = getProductsUrl({
    selectedKey: validKey,
    categoryName: validCategory?.subCategory?.SubCategoryName ||
      validCategory?.category?.CategoryName,
    page: legacyCategory
      ? location.state?.currentPage || 1
      : validKey === selectedKey
        ? page
        : 1,
    search,
    sort,
  });
  const extraParams = new URLSearchParams(location.search);
  ["category", "page", "search", "sort"].forEach((key) => extraParams.delete(key));
  const extraQuery = extraParams.toString();
  const normalizedUrl = filtersUrl + (extraQuery
    ? `${filtersUrl.includes("?") ? "&" : "?"}${extraQuery}` : "");
  const needsRedirect =
    `${location.pathname}${location.search}` !== normalizedUrl;

  useEffect(() => {
    if (!combinedCategories?.length) return;
    if (needsRedirect) {
      navigate(normalizedUrl, { replace: true, state: null });
    }
  }, [combinedCategories, needsRedirect, normalizedUrl, navigate]);

  useEffect(() => {
    const found = findByKey(combinedCategories, selectedKey);
    setOpenKeys(
      found && selectedKey !== "cat-0" ? [`cat-${found.category.Id}`] : [],
    );
  }, [combinedCategories, selectedKey]);

  useEffect(() => setSearchValue(search), [search]);

  const handleOpenChange = (keys) => {
    if (keys.length === 0) return;

    const latestKey = keys.find((k) => !openKeys.includes(k));

    if (!latestKey) return;

    setOpenKeys([latestKey]);
  };

  useEffect(() => {
    let active = true;
    const getCombinedCategories = async () => {
      try {
        const response = await publicDataServices.getCombinedCategories();
        if (response.data.statusCode !== 200) {
          throw new Error("Failed to fetch categories");
        }
        if (active) {
          setCombinedCategories([
            { Id: 0, CategoryName: "All" },
            ...JSON.parse(response.data.data),
          ]);
        }
      } catch (error) {
        if (active) {
          messageApi.error(
            error?.response?.data?.message || "Failed to fetch categories",
          );
          setIsPaginationLoading(false);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    getCombinedCategories();
    return () => {
      active = false;
    };
  }, [messageApi]);

  useEffect(() => {
    if (!combinedCategories.length || needsRedirect) return;
    let active = true;
    const getProducts = async () => {
      setIsPaginationLoading(true);
      try {
        const response = await publicDataServices.getProducts(
          (page - 1) * PAGE_SIZE,
          PAGE_SIZE,
          search,
          selectedKey,
          sort,
        );
        if (response.data.statusCode !== 200) {
          throw new Error("Failed to fetch products");
        }
        if (active) {
          const data = JSON.parse(response.data.data);
          const lastPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
          if (page > lastPage) {
            const lastPageUrl = getProductsUrl({
              selectedKey, categoryName, page: lastPage, search, sort,
            });
            navigate(lastPageUrl + (extraQuery
              ? `${lastPageUrl.includes("?") ? "&" : "?"}${extraQuery}` : ""), {
              replace: true,
            });
            return;
          }
          setProducts(data.products);
          setTotal(data.total);
        }
      } catch (error) {
        if (active) {
          setProducts([]);
          setTotal(0);
          messageApi.error(
            error?.response?.data?.message || "Failed to fetch products",
          );
        }
      } finally {
        if (active) setIsPaginationLoading(false);
      }
    };
    getProducts();
    return () => {
      active = false;
    };
  }, [combinedCategories, needsRedirect, selectedKey, categoryName, page, search, sort, extraQuery, navigate, messageApi]);

  const updateFilters = (patch) => {
    const nextFilters = { selectedKey, page, search, sort, ...patch };
    const nextCategory = findByKey(combinedCategories, nextFilters.selectedKey);
    navigate(getProductsUrl({
      ...nextFilters,
      categoryName: nextCategory?.subCategory?.SubCategoryName ||
        nextCategory?.category?.CategoryName,
    }));
  };

  const categoryLabel = (key, name) => (
    <Link
      to={getProductsUrl({ selectedKey: key, categoryName: name, search, sort })}
      onClick={(event) => {
        event.stopPropagation();
        if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          setOpen(false);
        }
      }}
      style={{
        backgroundColor: selectedKey === key ? "#FFDC00" : undefined,
        color: selectedKey === key ? "#000" : "inherit",
        fontWeight: selectedKey === key ? 500 : undefined,
        display: "block",
        padding: "5px 12px",
        borderRadius: 10,
      }}
    >
      {name}
    </Link>
  );

  const categoryMenuItems = combinedCategories.map((category) => {
    const key = `cat-${category.Id}`;
    return {
      key,
      label: categoryLabel(key, category.CategoryName),
      ...(category.SubCategories?.length
        ? {
            onTitleClick: () => updateFilters({ selectedKey: key, page: 1 }),
            children: category.SubCategories.map((sub) => ({
              key: `sub-${sub.Id}`,
              label: categoryLabel(`sub-${sub.Id}`, sub.SubCategoryName),
            })),
          }
        : {}),
    };
  });

  //#region add to cart helpers
  // useEffect(() => {
  //   setSelectedOptions(getDefaultSelectedOptions(productDetails));
  // }, [productDetails]);

  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);

  //#endregion

  return (
    <Space direction="vertical" className="page-container">
      <StaticSeoManager categoryName={categoryName} />
      {isLoading && (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined spin />}
          fullscreen
        />
      )}

      {contextHolder}

      <PageTitle
        headingLevel={1}
        title={categoryName
          ? categoryName
          : "Furniture Hardware & Kitchen Accessories"}
        subTitle={categoryName
          ? `Browse ${categoryName} from Teamstar. Explore product details and specifications to find products for your project in Malaysia.`
          : "Explore Teamstar's furniture fittings, cabinet hardware, drawer systems, kitchen storage and wardrobe accessories in Malaysia. Browse by category to find products for your furniture and renovation projects."}
        buttonText=""
      />

      {(isMobile || isTablet) && (
        <>
          <Button
            block
            icon={<AppstoreOutlined />}
            onClick={() => setOpen(true)}
            style={{ marginTop: isMobile ? 10 : undefined }}
          >
            Browse Categories
          </Button>

          <Drawer
            title="Product Categories"
            placement="left"
            onClose={() => setOpen(false)}
            open={open}
            styles={{
              body: {
                paddingLeft: 0,
                paddingRight: 50,
                paddingBottom: 0,
              },
            }}
          >
            <Menu
              mode="inline"
              openKeys={openKeys}
              selectedKeys={[selectedKey]}
              onOpenChange={handleOpenChange}
              onClick={({ key }) => {
                updateFilters({ selectedKey: key, page: 1 });
                setOpen(false);
              }}
              items={categoryMenuItems}
              style={{ border: 0 }}
            />

            {/* <Collapse accordion>
              {combinedCategories.map((cat) => {
                const catKey = `cat-${cat.Id}`;
                const hasSubs = cat.SubCategories?.length > 0;

                return (
                  <Panel header={cat.CategoryName} key={catKey}>
                    {hasSubs &&
                      cat.SubCategories.map((sub) => {
                        const subKey = `sub-${sub.Id}`;
                        return (
                          <div
                            key={subKey}
                            style={{
                              padding: "8px 12px",
                              cursor: "pointer",
                              fontWeight: selectedKey === subKey ? 600 : 400,
                              backgroundColor:
                                selectedKey === subKey ? "#FFDC00" : undefined,
                            }}
                            onClick={() => {
                              setSelectedKey(subKey);
                              setOpen(false); // close drawer
                            }}
                          >
                            {sub.SubCategoryName}
                          </div>
                        );
                      })}
                  </Panel>
                );
              })}
            </Collapse> */}
          </Drawer>
        </>
      )}

      <Flex
        style={{
          width: "100%",
        }}
        gap={20}
      >
        {!isMobile && !isTablet && (
          <Menu
            mode="inline"
            openKeys={openKeys}
            selectedKeys={[selectedKey]}
            onOpenChange={handleOpenChange}
            onClick={({ key }) => {
              updateFilters({ selectedKey: key, page: 1 });
              setOpen(false);
            }}
            items={categoryMenuItems}
            style={{ width: "auto", marginLeft: -30 }}
          />
        )}

        {isLoading || isPaginationLoading ? (
          <Spin spinning indicator={<LoadingOutlined spin />} />
        ) : (
          <Flex
            vertical
            style={{
              width: "100%",
            }}
          >
            <Flex justify="space-between">
              <Search
                placeholder="Search products..."
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={(value) => updateFilters({ search: value.trim(), page: 1 })}
                value={searchValue}
                enterButton
                style={{
                  width: 400,
                  marginBottom: 10,
                }}
              />

              <Select
                value={sort}
                defaultValue="date_asc"
                onChange={(value) => updateFilters({ sort: value, page: 1 })}
                style={{
                  width: 160,
                  marginRight: isMobile || isTablet ? 0 : "3vw",
                  marginLeft: isMobile ? 10 : 0,
                }}
              >
                <Select.Option value="date_asc">Newest</Select.Option>
                <Select.Option value="date_desc">Oldest</Select.Option>
                <Select.Option value="name_asc">Name ↑ </Select.Option>
                <Select.Option value="name_desc">Name ↓</Select.Option>
              </Select>
            </Flex>

            <List
              className="productGrid"
              locale={{ emptyText: "No product(s) available." }}
              // grid={{
              //   gutter: 16,
              //   xs: 1, // 1 column on extra small
              //   md: 2, // 2 columns on medium and up
              //   lg: 2, // 2 columns on large
              //   xl: 4, // 4 columns on extra large
              //   xxl: 5, // 5 columns on double extra large
              // }}
              dataSource={products}
              renderItem={(item) => {
                let splitImages = item.ImagesTag.split(";").map((img) =>
                  img.trim(),
                );
                const productUrl = getProductUrl(
                  item.ProductName,
                  item.Sku,
                  item.Id,
                );

                return (
                  <List.Item>
                    {(() => {
                      const card = (
                        <Card
                          styles={{
                            body: {
                              paddingTop: 10,
                              paddingBottom: 0,
                              width: "100%",
                              minWidth: 225,
                            },
                            actions: {
                              paddingLeft: 20,
                              paddingRight: 20,
                              paddingTop: 0,
                              paddingBottom: 0,
                              margin: 0,
                              borderTop: "none",
                            },
                          }}
                          style={{
                            cursor: "initial",
                            marginTop: screens.xs ? 10 : 10,
                          }}
                          hoverable={false}
                          cover={
                            <Image
                              width={screens.xs || screens.md ? "100%" : 240}
                              height={200}
                              alt={item.ProductName}
                              style={{ borderRadius: 4, objectFit: "contain" }}
                              src={settings.productImageFolder + splitImages[0]}
                            />
                          }
                          actions={[
                            // <Flex style={{ width: "100%" }} gap={6}>
                            //   <Button
                            //     type="primary"
                            //     block
                            //     style={{
                            //       marginTop: 0,
                            //       backgroundColor: "#002753",
                            //       color: "#fff",
                            //     }}
                            //     className="view-details-btn"
                            //     onClick={() => {
                            //       navigate(
                            //         `/product/${toSlug(
                            //           item.ProductName,
                            //         )}/${toSlug(item.Sku)}/${item.Id}`,
                            //       );
                            //     }}
                            //   >
                            //     View Details
                            //   </Button>

                            //   <Button
                            //     type="primary"
                            //     icon={<ShoppingCartOutlined />}
                            //     style={{
                            //       marginTop: 0,
                            //       backgroundColor: "#FFDC00",
                            //       color: "black",
                            //       padding: 15,
                            //     }}
                            //     className="add-to-cart-btn"
                            //     onClick={() => {}}
                            //   />
                            // </Flex>,

                            <Button
                              key="view"
                              type="primary"
                              block
                              style={{
                                marginTop: 0,
                                backgroundColor: "#002753",
                                color: "#fff",
                              }}
                              className="view-details-btn"
                              href={productUrl}
                              aria-label={`View details for ${item.ProductName}`}
                            >
                              View Details
                            </Button>,
                          ]}
                        >
                          <Typography.Paragraph
                            ellipsis={{
                              rows: 2,
                              tooltip: item.ProductName,
                            }}
                            style={{
                              width: "100%",
                              minHeight: 50.5,
                              margin: 0,
                              fontSize: 16,
                              color: "#002753",
                              fontWeight: "bold",
                            }}
                          >
                            <Typography.Link
                              href={productUrl}
                              style={{ color: "inherit" }}
                            >
                              {item.ProductName}
                            </Typography.Link>
                          </Typography.Paragraph>

                          <p
                            style={{
                              margin: 0,
                              marginTop: 5,
                              fontSize: 14,
                              color: "#6B7280",
                            }}
                          >
                            {item.Sku}
                          </p>
                        </Card>
                      );

                      // wrap with ribbons
                      let wrapped = card;

                      if (item.Promotions && item.Promotions.length > 0) {
                        wrapped = (
                          <div style={{ position: "relative" }}>
                            <div
                              style={{
                                position: "absolute",
                                top: 10,
                                left: -12,
                                background: "#FFDC00",
                                color: "#fff",
                                padding: "2px 10px",
                                transform: "rotate(-45deg)",
                                fontWeight: "bold",
                                zIndex: 1,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                borderRadius: 4,
                                color: "black",
                              }}
                            >
                              Promo
                              {/* {item.Promotions[0].DiscountPercent}% OFF */}
                            </div>

                            {wrapped}
                          </div>
                        );
                      }

                      if (item.RibbonText) {
                        wrapped = (
                          <Badge.Ribbon text={item.RibbonText} color="red">
                            {wrapped}
                          </Badge.Ribbon>
                        );
                      }

                      return wrapped;
                    })()}
                  </List.Item>
                );
              }}
            />

            <Flex justify="center" style={{ marginTop: 20 }}>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                itemRender={(targetPage, type, originalElement) => {
                  if (!["page", "prev", "next"].includes(type)) {
                    return originalElement;
                  }
                  const disabled = type === "prev"
                    ? page <= 1
                    : type === "next"
                      ? page >= Math.max(1, Math.ceil(total / PAGE_SIZE))
                      : false;
                  if (disabled) return originalElement;
                  return (
                    <Link
                      to={getProductsUrl({ selectedKey, categoryName, page: targetPage, search, sort })}
                      aria-label={type === "page" ? `Page ${targetPage}` : `${type === "prev" ? "Previous" : "Next"} page`}
                      aria-current={type === "page" && targetPage === page ? "page" : undefined}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {type === "page" ? targetPage : type === "prev" ? "‹" : "›"}
                    </Link>
                  );
                }}
                onChange={(nextPage) => updateFilters({ page: nextPage })}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Space>
  );
};

export default Products;
